import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TreeSelect } from 'primereact/treeselect';
import { TreeNode } from 'primereact/treenode';
import { Button } from 'primereact/button';
import { Api } from '../../general/api';
import { ProtectedStorageEntryDto } from '../../general/dtos';

interface Props {
    api: Api;
    value: string;
    onChange: (path: string) => void;
    onValidityChange: (valid: boolean) => void;
}

/** A dialog-scoped cache: directories are fetched only when opened, never recursively up front. */
export function ProtectedFileSelect({ api, value, onChange, onValidityChange }: Props) {
    const cache = useRef(new Map<string, ProtectedStorageEntryDto[]>());
    const pending = useRef(new Map<string, Promise<ProtectedStorageEntryDto[] | null>>());
    const controller = useRef(new AbortController());
    const restoration = useRef(0);
    const [, setRevision] = useState(0);
    const [loading, setLoading] = useState<Set<string>>(new Set());
    const [errors, setErrors] = useState<Map<string, string>>(new Map());
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    useEffect(() => {
        controller.current = new AbortController();
        cache.current.clear();
        pending.current.clear();
        setLoading(new Set()); setErrors(new Map()); setExpanded({});
        setRevision(current => current + 1);
        return () => controller.current.abort();
    }, [api]);

    const load = useCallback((path: string): Promise<ProtectedStorageEntryDto[] | null> => {
        if (cache.current.has(path)) return Promise.resolve(cache.current.get(path)!);
        if (pending.current.has(path)) return pending.current.get(path)!;
        const signal = controller.current.signal;
        setLoading(current => new Set(current).add(path));
        setErrors(current => { const next = new Map(current); next.delete(path); return next; });
        const request = api.listProtectedStorage(path, undefined, signal).then(result => {
            if (signal.aborted) return null;
            cache.current.set(path, result.entries);
            setRevision(current => current + 1);
            return result.entries;
        }).catch(() => {
            if (!signal.aborted) setErrors(current => new Map(current).set(path, 'Unable to load ' + (path || 'protected storage') + '.'));
            return null;
        }).finally(() => {
            if (!signal.aborted) {
                pending.current.delete(path);
                setLoading(current => { const next = new Set(current); next.delete(path); return next; });
            }
        });
        pending.current.set(path, request);
        return request;
    }, [api]);

    const restore = useCallback(async () => {
        const signal = controller.current.signal;
        const attempt = ++restoration.current;
        const current = () => !signal.aborted && restoration.current === attempt;
        onValidityChange(false);
        let entries = await load('');
        if (!value || !entries || !current()) return;
        const parts = value.split('/');
        for (let index = 1; index < parts.length; index++) {
            const parent = parts.slice(0, index).join('/');
            if (!entries.some(entry => entry.path === parent && entry.type === 'folder')) {
                const directory = parts.slice(0, index - 1).join('/');
                setErrors(current => new Map(current).set(directory, 'The selected file is no longer available.'));
                return;
            }
            setExpanded(current => ({ ...current, [parent]: true }));
            entries = await load(parent);
            if (!entries || !current()) return;
        }
        const exists = entries.some(entry => entry.path === value && entry.type === 'file');
        onValidityChange(exists);
        if (!exists) setErrors(current => new Map(current).set(parts.slice(0, -1).join('/'), 'The selected file is no longer available.'));
    }, [load, value, onValidityChange]);

    useEffect(() => {
        if (value) void restore();
        else { restoration.current++; onValidityChange(false); }
    }, [restore, value, onValidityChange]);

    const nodes = (path: string): TreeNode[] => (cache.current.get(path) ?? []).map(entry => ({
        key: entry.path, label: entry.name, data: entry.type,
        selectable: entry.type === 'file',
        leaf: entry.type === 'file' || (cache.current.has(entry.path) && cache.current.get(entry.path)!.length === 0),
        icon: loading.has(entry.path) ? 'pi pi-spinner pi-spin' : entry.type === 'folder' ? 'pi pi-folder' : 'pi pi-file',
        children: entry.type === 'folder' && cache.current.has(entry.path) ? nodes(entry.path) : undefined,
    }));
    return <TreeSelect inputId="editor-protected-file" ariaLabel="Protected file" className="w-full"
        options={nodes('')} value={value || null} selectionMode="single" expandedKeys={expanded}
        onToggle={event => setExpanded(event.value)} onNodeExpand={event => void load(String(event.node.key))}
        onShow={() => { void restore(); }} placeholder="Select a file" scrollHeight="20rem"
        dropdownIcon={loading.size > 0 ? 'pi pi-spinner pi-spin' : undefined}
        valueTemplate={() => <span title={value}>{value || 'Select a file'}</span>}
        emptyMessage={loading.has('') ? 'Loading files…' : errors.size ? 'Files could not be loaded.' : 'This folder is empty.'}
        panelHeaderTemplate={<div className="px-3 py-2">
            {loading.size > 0 && <span role="status"><i className="pi pi-spinner pi-spin mr-2" aria-hidden="true" />Loading files…</span>}
            {[...errors].map(([path, message]) => <div key={path} className="flex align-items-center gap-2">
                <span role="alert">{message}</span><Button label="Retry" size="small" text
                    onClick={() => { cache.current.delete(path); void load(path).then(() => { if (value) void restore(); }); }} />
            </div>)}
        </div>}
        onChange={event => {
            if (typeof event.value !== 'string') return;
            const entry = [...cache.current.values()].flat().find(item => item.path === event.value);
            if (entry?.type === 'file') {
                restoration.current++;
                setErrors(current => new Map([...current].filter(([, message]) => message !== 'The selected file is no longer available.')));
                onChange(entry.path); onValidityChange(true);
            }
        }} />;
}
